import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  ArrowLeft, FileText, Building2, Calendar, User, 
  CheckCircle, XCircle, AlertTriangle, Lightbulb, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if this is an interactive report based on ID prefix
  const isInteractiveReport = id?.startsWith('irep_');

  const loadReport = useCallback(async () => {
    try {
      // Use correct endpoint based on report type
      const endpoint = isInteractiveReport 
        ? `${API}/interactive-reports/${id}`
        : `${API}/reports/${id}`;
      
      const response = await axios.get(endpoint, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      toast.error("Rapor bulunamadı");
      navigate("/reports");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, isInteractiveReport]);

  useEffect(() => {
    // If it's an interactive report, redirect to the interactive report page
    if (isInteractiveReport) {
      navigate(`/interactive-report/${id}`, { replace: true });
      return;
    }
    loadReport();
  }, [loadReport, isInteractiveReport, id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!report) return null;

  const statusStyles = {
    draft: "bg-gray-100 text-gray-800",
    submitted: "bg-blue-100 text-blue-800",
    under_review: "bg-yellow-100 text-yellow-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-red-100 text-red-800"
  };

  const statusLabels = {
    draft: "Taslak",
    submitted: "Gönderildi",
    under_review: "İnceleniyor",
    approved: "Onaylandı",
    rejected: "Reddedildi"
  };

  const findingLabels = {
    major: "Majör Uygunsuzluk",
    minor: "Minör Uygunsuzluk",
    observation: "Gözlem",
    opportunity: "İyileştirme Fırsatı"
  };

  const renderFindingIcon = (type) => {
    if (type === "major") return <XCircle className="w-5 h-5 text-red-500" />;
    if (type === "minor") return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    if (type === "observation") return <CheckCircle className="w-5 h-5 text-blue-500" />;
    if (type === "opportunity") return <Lightbulb className="w-5 h-5 text-green-500" />;
    return <CheckCircle className="w-5 h-5 text-gray-500" />;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="report-detail">
      <Button variant="ghost" onClick={() => navigate("/reports")} className="mb-4">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Raporlara Dön
      </Button>

      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-[#111827]">{report.title}</CardTitle>
                <p className="text-[#4B5563] mt-1">{report.audit_title}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 text-sm rounded-full ${statusStyles[report.status]}`}>
                {statusLabels[report.status]}
              </span>
              <Button variant="outline" className="gap-2">
                <Download className="w-4 h-4" />
                İndir
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
              <Building2 className="w-5 h-5 text-[#1A4D2E]" />
              <div>
                <p className="text-xs text-[#9CA3AF]">Müşteri</p>
                <p className="font-medium text-[#111827]">{report.customer_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
              <User className="w-5 h-5 text-[#1A4D2E]" />
              <div>
                <p className="text-xs text-[#9CA3AF]">Oluşturan</p>
                <p className="font-medium text-[#111827]">{report.created_by_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
              <Calendar className="w-5 h-5 text-[#1A4D2E]" />
              <div>
                <p className="text-xs text-[#9CA3AF]">Tarih</p>
                <p className="font-medium text-[#111827]">
                  {format(new Date(report.created_at), "dd MMMM yyyy", { locale: tr })}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8F3EE]">
        <CardHeader>
          <CardTitle className="text-lg text-[#111827]">Rapor İçeriği</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none text-[#4B5563] whitespace-pre-wrap">
            {report.content}
          </div>
        </CardContent>
      </Card>

      {report.findings && report.findings.length > 0 && (
        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">Bulgular</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.findings.map((finding, idx) => (
              <div key={idx} className="flex items-start gap-3 p-4 rounded-lg bg-[#F9FAFB]">
                {renderFindingIcon(finding.type)}
                <div>
                  <p className="font-medium text-[#111827]">{findingLabels[finding.type]}</p>
                  <p className="text-sm text-[#4B5563] mt-1">{finding.description}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {report.recommendations && report.recommendations.length > 0 && (
        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">Öneriler</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {report.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2 text-[#4B5563]">
                  <CheckCircle className="w-4 h-4 text-[#1A4D2E] mt-1 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {report.reviewed_by && (
        <Card className="border-[#E8F3EE]">
          <CardHeader>
            <CardTitle className="text-lg text-[#111827]">İnceleme Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
                <User className="w-5 h-5 text-[#1A4D2E]" />
                <div>
                  <p className="text-xs text-[#9CA3AF]">İnceleyen</p>
                  <p className="font-medium text-[#111827]">{report.reviewed_by_name}</p>
                </div>
              </div>
              {report.reviewed_at && (
                <div className="flex items-center gap-3 p-3 rounded-lg bg-[#F9FAFB]">
                  <Calendar className="w-5 h-5 text-[#1A4D2E]" />
                  <div>
                    <p className="text-xs text-[#9CA3AF]">İnceleme Tarihi</p>
                    <p className="font-medium text-[#111827]">
                      {format(new Date(report.reviewed_at), "dd MMMM yyyy HH:mm", { locale: tr })}
                    </p>
                  </div>
                </div>
              )}
            </div>
            {report.review_notes && (
              <div className="p-4 rounded-lg bg-[#F9FAFB]">
                <p className="text-xs text-[#9CA3AF] mb-2">İnceleme Notları</p>
                <p className="text-[#4B5563]">{report.review_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportDetail;
